/*
  Zeero product gallery — mobile layout (2026-10-13)
  One large photo on top, two below. With 4+ photos the window steps forward:
  photo 2 moves to the top, 3 and 4 move below, and so on, wrapping at the end.
  Steps on swipe, on tapping a small photo, and (optionally) on a timer.
  Only the CSS order/visibility changes — the DOM order stays the theme's, so
  media-gallery.js, the zoom modal and the desktop layout are untouched.
*/
(() => {
  const mobile = window.matchMedia('(max-width: 749px)');
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const DURATION = 450;
  const EASE = 'cubic-bezier(0.2, 0.7, 0.2, 1)';

  function setup(wrapper) {
    const gallery = wrapper.querySelector('media-gallery');
    const list = gallery && gallery.querySelector('.product__media-list');
    if (!list || list.dataset.zpGallery) return;
    list.dataset.zpGallery = 'ready';
    gallery.classList.add('zp-gal');

    const autoplay = wrapper.dataset.zpAutoplay === 'true';
    const delay = Math.max(2, parseInt(wrapper.dataset.zpDelay, 10) || 4) * 1000;
    let start = 0;
    let timer = null;
    let inView = true;
    let touching = false;

    const items = () => [...list.children].filter((li) => li.matches('.product__media-item:not(.hidden):not(.zp-gal__ghost)'));

    function assign() {
      const all = items();
      [...list.children].forEach((li) => li.removeAttribute('data-zp-slot'));
      if (!all.length) return;
      start = ((start % all.length) + all.length) % all.length;
      const shown = Math.min(3, all.length);
      for (let i = 0; i < shown; i++) all[(start + i) % all.length].setAttribute('data-zp-slot', i);
    }

    // FLIP: remember where each photo was, re-slot, then animate from old to new box.
    function go(next, animate = true) {
      const all = items();
      if (all.length < 2) return;
      next = ((next % all.length) + all.length) % all.length;
      if (next === start) return;
      if (!animate || reduced.matches || !mobile.matches || !list.animate) {
        start = next;
        assign();
        return;
      }
      const before = new Map(all.map((li) => [li, li.hasAttribute('data-zp-slot') ? li.getBoundingClientRect() : null]));
      const listBox = list.getBoundingClientRect();
      const ghosts = [];
      start = next;
      // Photos leaving the window fade out as a static copy so the space never flashes empty.
      all.forEach((li) => {
        const box = before.get(li);
        if (box && !isShownAfter(li, all)) {
          const ghost = li.cloneNode(true);
          ghost.removeAttribute('id');
          ghost.removeAttribute('data-zp-slot');
          ghost.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
          ghost.setAttribute('aria-hidden', 'true');
          ghost.className += ' zp-gal__ghost';
          Object.assign(ghost.style, {
            left: `${box.left - listBox.left}px`,
            top: `${box.top - listBox.top}px`,
            width: `${box.width}px`,
            height: `${box.height}px`,
          });
          list.appendChild(ghost);
          ghosts.push(ghost);
        }
      });
      assign();
      ghosts.forEach((g) => g.animate([{ opacity: 1 }, { opacity: 0 }], { duration: DURATION, easing: EASE }).finished.then(() => g.remove(), () => g.remove()));
      all.forEach((li) => {
        if (!li.hasAttribute('data-zp-slot')) return;
        const a = before.get(li);
        const b = li.getBoundingClientRect();
        if (!a) {
          li.animate([{ opacity: 0 }, { opacity: 1 }], { duration: DURATION, easing: EASE });
          return;
        }
        if (Math.abs(a.left - b.left) < 1 && Math.abs(a.top - b.top) < 1 && Math.abs(a.width - b.width) < 1) return;
        // Large and small photos have different shapes: scale to cover the old box, then crop to it (centred).
        const s = Math.max(a.width / b.width, a.height / b.height);
        const insetX = Math.max(0, (b.width - a.width / s) / 2);
        const insetY = Math.max(0, (b.height - a.height / s) / 2);
        const dx = a.left - b.left - insetX * s;
        const dy = a.top - b.top - insetY * s;
        const radius = parseFloat(getComputedStyle(li.querySelector('.product-media-container') || li).borderTopLeftRadius) || 0;
        li.animate(
          [
            {
              transformOrigin: '0 0',
              transform: `translate(${dx}px, ${dy}px) scale(${s})`,
              clipPath: `inset(${insetY}px ${insetX}px round ${(radius / s).toFixed(2)}px)`,
            },
            { transformOrigin: '0 0', transform: 'none', clipPath: `inset(0px 0px round ${radius}px)` },
          ],
          { duration: DURATION, easing: EASE }
        );
      });
    }

    function isShownAfter(li, all) {
      const i = all.indexOf(li);
      const n = all.length;
      const s = ((start % n) + n) % n;
      return (i - s + n) % n < Math.min(3, n);
    }

    function schedule() {
      clearTimeout(timer);
      timer = null;
      if (!autoplay || reduced.matches || !mobile.matches || !inView || touching || document.hidden) return;
      if (items().length < 4) return;
      timer = setTimeout(() => {
        go(start + 1);
        schedule();
      }, delay);
    }

    // Tap a small photo → it becomes the large one (instead of opening the zoom modal).
    list.addEventListener(
      'click',
      (e) => {
        if (!mobile.matches) return;
        const li = e.target.closest('.product__media-item[data-zp-slot]');
        if (!li || li.dataset.zpSlot === '0') return;
        e.preventDefault();
        e.stopPropagation();
        go(items().indexOf(li));
        schedule();
      },
      true
    );

    // Horizontal swipe → next / previous; vertical drags scroll the page as usual.
    let x0 = 0;
    let y0 = 0;
    list.addEventListener(
      'touchstart',
      (e) => {
        touching = true;
        clearTimeout(timer);
        x0 = e.touches[0].clientX;
        y0 = e.touches[0].clientY;
      },
      { passive: true }
    );
    list.addEventListener(
      'touchend',
      (e) => {
        touching = false;
        const t = e.changedTouches[0];
        const dx = t.clientX - x0;
        const dy = t.clientY - y0;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5 && mobile.matches) {
          go(start + (dx < 0 ? 1 : -1));
        }
        schedule();
      },
      { passive: true }
    );
    list.addEventListener('touchcancel', () => {
      touching = false;
      schedule();
    });

    // A variant change makes another photo active (media-gallery.js) → show it on top.
    new MutationObserver((records) => {
      records.forEach((r) => {
        const li = r.target;
        if (li.parentElement !== list || !li.classList.contains('is-active')) return;
        if ((r.oldValue || '').includes('is-active')) return;
        const i = items().indexOf(li);
        if (i > -1 && li.dataset.zpSlot !== '0') {
          go(i, false);
          schedule();
        }
      });
      if (records.some((r) => (r.oldValue || '').includes('hidden') !== r.target.classList.contains('hidden'))) assign();
    }).observe(list, { attributes: true, attributeFilter: ['class'], attributeOldValue: true, subtree: true });

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => {
        inView = entry.isIntersecting;
        schedule();
      }).observe(list);
    }
    document.addEventListener('visibilitychange', schedule);
    mobile.addEventListener('change', schedule);
    reduced.addEventListener('change', schedule);

    assign();
    schedule();
  }

  function init(root = document) {
    root.querySelectorAll('.product__media-wrapper[data-zp-gallery]').forEach(setup);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
